/**
 * Shares the current visitor's privacy choices between settings and tracking controls.
 * Loads remembered choices and responds to account switches and changes in other tabs.
 */
import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { createPrivacyChoices, trackingPurposes, type PrivacyChoices } from "@/types/privacyChoices"
import { privacyStorageKey, readPrivacyChoices, savePrivacyChoices } from "@/utils/privacyStorage"

export const usePrivacyStore = defineStore("privacy", () => {
	const choices = ref<PrivacyChoices>(createPrivacyChoices())
	const hasChoice = ref(false)
	const isSettingsOpen = ref(false)
	const storageError = ref(false)
	const showBanner = computed(() => !hasChoice.value && !isSettingsOpen.value)
	let scope = "guest"
	let channel: BroadcastChannel | null = null

	function loadChoices(): void {
		const savedChoices = readPrivacyChoices(scope)
		const next = savedChoices ?? createPrivacyChoices()
		if (trackingPurposes.some(purpose => choices.value[purpose] !== next[purpose])) choices.value = next
		hasChoice.value = savedChoices !== null
	}

	function initialize(currentScope: string): () => void {
		scope = currentScope
		loadChoices()
		function onStorage(event: StorageEvent): void {
			if (event.key === privacyStorageKey || event.key === null) loadChoices()
		}
		window.addEventListener("storage", onStorage)
		if (typeof BroadcastChannel !== "undefined") {
			try {
				channel = new BroadcastChannel("landerz.privacyChoices")
				channel.onmessage = event => {
					if (event.data === "declined") {
						storageError.value = true
						choices.value = createPrivacyChoices()
						hasChoice.value = true
					}
				}
			} catch {
				/* The storage listener still works when channels are unavailable. */
			}
		}
		const expiryCheck = window.setInterval(loadChoices, 60_000)
		return () => {
			window.removeEventListener("storage", onStorage)
			window.clearInterval(expiryCheck)
			channel?.close()
			channel = null
		}
	}

	function changeScope(currentScope: string): void {
		if (scope === currentScope) return
		scope = currentScope
		// Discard the previous account's unsaved settings, including when no
		// tracking runtime is active and the page does not need to refresh.
		isSettingsOpen.value = false
		// Stop the previous account's runtime even if this browser already holds
		// an equivalent choice for the next account. A refresh builds fresh SDKs.
		choices.value = createPrivacyChoices()
		hasChoice.value = false
		// Restore only this account's saved choice. The synchronous tracking
		// watcher has already stopped the previous runtime before any grant loads.
		loadChoices()
	}

	function saveChoices(selected: PrivacyChoices): boolean {
		const next = { ...selected }
		const saved = savePrivacyChoices(scope, next)
		storageError.value = !saved
		if (!saved) {
			try {
				channel?.postMessage("declined")
			} catch {
				/* Keep this tab denied even if the channel fails. */
			}
		}
		// Refusal works even if storage is blocked. Grants need a durable choice so
		// that reloads and other tabs can enforce withdrawal consistently.
		if (!saved && trackingPurposes.some(purpose => next[purpose])) {
			choices.value = createPrivacyChoices()
			hasChoice.value = false
			return false
		}
		choices.value = next
		hasChoice.value = true
		isSettingsOpen.value = false
		return true
	}

	function openSettings(): void {
		storageError.value = false
		isSettingsOpen.value = true
	}

	return {
		choices,
		hasChoice,
		isSettingsOpen,
		storageError,
		showBanner,
		initialize,
		changeScope,
		saveChoices,
		openSettings,
	}
})
